import type { HostsSummary, InfraOverview, StorageHost, StorageSummary } from '@/db/api'
import { hostIssues, type Issue } from './hosts'
import { fillOf, pctOf, shareOf } from './storage'

/**
 * The infra pack's Overview band (views/Infra.tsx), pure: each estate
 * (Hetzner, Home, Oracle Cloud, Object storage) green, amber or red from what
 * the page already reads. Red is something down or failing: a critical alert
 * naming one of its machines, a host not reporting, a drive failing SMART, a
 * disk past 95%. Amber is something to look at: a warning alert, a disk past
 * 85%, memory, CPU or load past their lines (lib/hosts.ts), a backup or its
 * offsite copy over 26 hours old. An estate nothing has been read for yet is
 * unknown, never green.
 *
 * Alerts are placed by the machines they name (internal/infra's `hosts`,
 * matched to a host's name or address); one that names none (the security
 * scans, a namespace's pods) belongs to no estate and stays in Alerts.
 */

export type EstateId = 'hetzner' | 'home' | 'oracle' | 'object'
export type Status = 'ok' | 'warn' | 'critical' | 'unknown'

export interface EstateCard {
  id: EstateId
  name: string
  status: Status
  /** what made it amber or red, worst first, at most MAX_REASONS */
  reasons: string[]
}

export const ESTATES: { id: EstateId; name: string }[] = [
  { id: 'hetzner', name: 'Hetzner' },
  { id: 'home', name: 'Home' },
  { id: 'oracle', name: 'Oracle Cloud' },
  { id: 'object', name: 'Object storage' },
]

export const MAX_REASONS = 2

export const STATUS_WORDS: Record<Status, string> = {
  ok: 'All good',
  warn: 'Needs a look',
  critical: 'Something is wrong',
  unknown: 'Not read',
}

// Storage's machines, which Hosts judges when it has answered; the rest
// (cloud volumes, the Storage Box, buckets) only Storage sees
const MACHINE_ROLES = new Set(['NAS', 'Hypervisor', 'Cluster node', 'Host'])

const storageIssues = (h: StorageHost): Issue[] => {
  const out: Issue[] = []
  if (h.down) out.push({ severity: 'critical', text: `${h.name} is not reporting` })
  for (const d of h.disks) {
    const fill = fillOf(shareOf(d))
    // worded as internal/storage's warnings are
    const what =
      h.role === 'Cloud volumes'
        ? `Volume ${d.name}`
        : h.role === 'Storage Box'
          ? 'The Storage Box'
          : `${h.name} ${d.name}`
    if (d.smart === 'failing') out.push({ severity: 'critical', text: `${what} fails its SMART check` })
    if (fill !== 'ok')
      out.push({
        severity: fill === 'critical' ? 'critical' : 'warning',
        text: `${what} is ${pctOf(shareOf(d))}% full`,
      })
  }
  for (const d of h.drives ?? [])
    if (!d.healthy)
      out.push({ severity: 'critical', text: `${h.name}: drive ${d.name} fails its SMART check` })
  return out
}

/**
 * The four cards. Each source may be missing (still loading, or failed); an
 * estate is judged on whatever has answered.
 */
export const estateCards = (o: {
  infra?: InfraOverview | null
  hosts?: HostsSummary | null
  storage?: StorageSummary | null
}): EstateCard[] => {
  const issues: Record<EstateId, Issue[]> = { hetzner: [], home: [], oracle: [], object: [] }
  const read = new Set<EstateId>()
  const add = (id: string, i: Issue) => {
    if (id in issues) issues[id as EstateId].push(i)
  }

  // the hosts: every machine, and where each lives (for placing alerts)
  const placed = new Map<string, EstateId>()
  if (o.hosts) {
    for (const id of ['hetzner', 'home', 'oracle'] as const) read.add(id)
    for (const h of o.hosts.hosts) {
      if (h.estate === 'other') continue
      placed.set(h.name, h.estate)
      if (h.address) placed.set(h.address, h.estate)
      for (const i of hostIssues(h)) add(h.estate, i)
    }
  }

  // storage: the stores Hosts does not see, and every machine when Hosts has not answered
  if (o.storage) {
    for (const id of ['hetzner', 'home', 'oracle', 'object'] as const) read.add(id)
    for (const site of o.storage.sites)
      for (const h of site.hosts) {
        if (o.hosts && MACHINE_ROLES.has(h.role)) continue
        for (const i of storageIssues(h)) add(site.id, i)
      }
    for (const b of o.storage.backups) {
      const what = b.kind === 'postgres' ? `Postgres ${b.name}'s base backup` : `The ${b.name} backup`
      const home: EstateId = b.where === 'Object storage' ? 'object' : 'hetzner'
      if (b.stale)
        add(home, {
          severity: 'warning',
          text: b.last ? `${what} is over 26 hours old` : `${what} never succeeded`,
        })
      // the offsite copy goes to the bucket
      if (b.offsite_stale)
        add('object', { severity: 'warning', text: `${what}'s offsite copy is over 26 hours old` })
    }
  }

  // alerts that name a machine of the estate
  if (o.infra?.alerts.state === 'ok')
    for (const g of o.infra.alerts.groups) {
      if (g.severity !== 'critical' && g.severity !== 'warning') continue
      const estates = new Set<EstateId>()
      for (const h of g.hosts ?? []) {
        const e = placed.get(h)
        if (e) estates.add(e)
      }
      for (const e of estates) {
        read.add(e)
        add(e, { severity: g.severity, text: `${g.alert} is firing` })
      }
    }

  return ESTATES.map(({ id, name }) => {
    const list = dedupe(issues[id]).sort((a, b) => sev(a) - sev(b))
    const status: Status = !read.has(id)
      ? 'unknown'
      : list.some((i) => i.severity === 'critical')
        ? 'critical'
        : list.length
          ? 'warn'
          : 'ok'
    return { id, name, status, reasons: list.slice(0, MAX_REASONS).map((i) => i.text) }
  })
}

const sev = (i: Issue) => (i.severity === 'critical' ? 0 : 1)

const dedupe = (xs: Issue[]): Issue[] => {
  const seen = new Set<string>()
  return xs.filter((i) => !seen.has(i.text) && !!seen.add(i.text))
}
